import { ComponentProps, Ref, UIEvent } from 'react';

import { cn } from '@/utils/tailwind';

type TableProps = ComponentProps<'table'> & {
  containerClassName?: string;
  containerRef?: Ref<HTMLDivElement>;
  onScroll?: (event: UIEvent<HTMLDivElement>) => void;
};
type TableHeaderProps = ComponentProps<'thead'>;
type TableHeadProps = ComponentProps<'th'>;
type TableBodyProps = ComponentProps<'tbody'>;
type TableRowProps = ComponentProps<'tr'>;
type TableCellProps = ComponentProps<'td'>;
type TableCaptionProps = ComponentProps<'caption'>;

const Table = ({ className, containerClassName, containerRef, onScroll, ...props }: TableProps) => {
  return (
    <div
      ref={containerRef}
      data-slot="table-container"
      className={cn(
        'relative w-full overflow-x-auto overflow-y-visible rounded-[12px] border border-custom-warm-grey-200',
        containerClassName
      )}
      onScroll={onScroll}
    >
      <table
        data-slot="table"
        className={cn('w-full table-auto border-collapse', className)}
        {...props}
      />
    </div>
  );
};

const TableHeader = ({ className, ...props }: TableHeaderProps) => {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        'sticky top-0 z-20 border-b border-b-custom-warm-grey-200 bg-custom-warm-grey-150',
        className
      )}
      {...props}
    />
  );
};

const TableHead = ({ className, ...props }: TableHeadProps) => {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-body-14-medium !text-custom-warm-grey-800 select-none text-left px-5 py-3 bg-custom-warm-grey-150 whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className
      )}
      {...props}
    />
  );
};

const TableBody = ({ className, ...props }: TableBodyProps) => {
  return <tbody data-slot="table-body" className={cn('', className)} {...props} />;
};

const TableRow = ({ className, ...props }: TableRowProps) => {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'transition-colors bg-custom-warm-grey-25 hover:bg-custom-warm-grey-100',
        className
      )}
      {...props}
    />
  );
};

const TableCell = ({ className, ...props }: TableCellProps) => {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'px-5 py-[14px] text-left text-body-14-regular !text-custom-warm-grey-800',
        className
      )}
      {...props}
    />
  );
};

const TableCaption = ({ className, ...props }: TableCaptionProps) => {
  return <caption data-slot="table-caption" className={cn('', className)} {...props} />;
};

export { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow };
